import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {useAuthUser} from '_contexts/AuthUserProvider';
import {
  deletePgHistoryAgentThread,
  getPgHistoryAgentThread,
  getPgHistoryAgentThreads,
  getSharedPgHistoryAgentThreads,
  PgHistoryAgentEvent,
  PgHistoryAgentPrivateThreadCursor,
  PgHistoryAgentSharedThreadCursor,
  PgHistoryAgentThreadDetail,
  PgHistoryAgentThreadSummary,
  PgHistoryAgentThreadTurn,
  setPgHistoryAgentThreadVisibility,
  streamPgHistoryAgentChat
} from '_logics/LFCSourceUtil';
import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import {useTranslation} from 'react-i18next';
import AgentCostBadge from './AgentCostBadge';
import AgentToolLog from './AgentToolLog';
import AiMarkdownViewer from '_components/common/AiMarkdownViewer';

type ToolEvent = Extract<PgHistoryAgentEvent, {type: 'tool'}>;

const jobStatusTranslationKey = {
  queued: '実行待ち',
  running: '実行中',
  completed: '完了',
  failed: '失敗',
  cancelled: 'キャンセル済み'
} satisfies Record<PgHistoryAgentThreadSummary['status'], string>;

export type AgentChatTurn = {
  id: string;
  prompt: string;
  answer: string;
  tools: ToolEvent[];
  error: string | null;
  done: boolean;
  requestCostUsd: string | null;
  sessionCostUsd: string | null;
};

export type AgentChatThread = {
  id: string;
  sessionId: string;
  title: string;
  createdAt: string;
  turns: AgentChatTurn[];
  activeTurnId: string | null;
  readOnly?: boolean;
};

export type AgentChatEventState = {
  threads: AgentChatThread[];
  activeThreadId: string | null;
};

type AgentChatReducerEvent =
  | PgHistoryAgentEvent
  | {type: 'newThread'; id: string; sessionId: string; createdAt: string}
  | {type: 'reset'}
  | {type: 'removeThread'; id: string}
  | {type: 'start'; id: string; prompt: string}
  | {type: 'stop'}
  | {type: 'restoreThread'; detail: PgHistoryAgentThreadDetail; readOnly: boolean};

const updateActiveThread = (
  state: AgentChatEventState,
  update: (thread: AgentChatThread) => AgentChatThread
): AgentChatEventState => ({
  ...state,
  threads: state.threads.map(thread =>
    thread.id === state.activeThreadId ? update(thread) : thread
  )
});

const updateActiveTurn = (
  state: AgentChatEventState,
  update: (turn: AgentChatTurn) => AgentChatTurn
): AgentChatEventState => ({
  ...state,
  threads: state.threads.map(thread => {
    if (thread.id !== state.activeThreadId) return thread;
    return {
      ...thread,
      turns: thread.turns.map(turn => (turn.id === thread.activeTurnId ? update(turn) : turn))
    };
  })
});

const isTerminalAgentTurn = (turn: PgHistoryAgentThreadTurn) =>
  turn.status === 'completed' ||
  turn.status === 'failed' ||
  turn.status === 'cancelled' ||
  turn.events.some(event => event.type === 'done');

const restoreAgentChatTurn = (turn: PgHistoryAgentThreadTurn): AgentChatTurn => {
  const restored = turn.events.reduce<AgentChatTurn>(
    (current, event) => {
      if (event.type === 'tool') {
        return {...current, tools: [...current.tools, event]};
      }
      if (event.type === 'delta') {
        return {...current, answer: current.answer + event.text};
      }
      if (event.type === 'usage') {
        return {
          ...current,
          requestCostUsd: event.estimatedCostUsd,
          sessionCostUsd: event.sessionCostUsd
        };
      }
      if (event.type === 'error') {
        return {...current, error: event.message};
      }
      return current;
    },
    {
      id: turn.jobId,
      prompt: turn.prompt,
      answer: '',
      tools: [],
      error: turn.errorMessage,
      done: isTerminalAgentTurn(turn),
      requestCostUsd: null,
      sessionCostUsd: null
    }
  );
  return restored;
};

export const reduceAgentChatEvent = (
  state: AgentChatEventState,
  event: AgentChatReducerEvent
): AgentChatEventState => {
  if (event.type === 'reset') {
    return {threads: [], activeThreadId: null};
  }
  if (event.type === 'newThread') {
    const thread: AgentChatThread = {
      id: event.id,
      sessionId: event.sessionId,
      title: 'New Channel',
      createdAt: event.createdAt,
      turns: [],
      activeTurnId: null
    };
    return {...state, threads: [thread, ...state.threads], activeThreadId: event.id};
  }
  if (event.type === 'removeThread') {
    const removesActiveThread = state.threads.some(
      thread =>
        thread.id === state.activeThreadId &&
        (thread.id === event.id || thread.sessionId === event.id)
    );
    return {
      ...state,
      threads: state.threads.filter(
        thread => thread.id !== event.id && thread.sessionId !== event.id
      ),
      activeThreadId: removesActiveThread ? null : state.activeThreadId
    };
  }
  if (event.type === 'restoreThread') {
    const {detail} = event;
    const turns = detail.turns.map(restoreAgentChatTurn);
    const thread: AgentChatThread = {
      id: detail.sessionId,
      sessionId: detail.sessionId,
      title: detail.title,
      createdAt: new Date(detail.createdAt * 1000).toLocaleString(),
      turns,
      activeTurnId: turns.reduce<string | null>(
        (activeTurnId, turn) => (turn.done ? activeTurnId : turn.id),
        null
      ),
      readOnly: event.readOnly
    };
    return {
      ...state,
      threads: [
        thread,
        ...state.threads.filter(
          current => current.id !== detail.sessionId && current.sessionId !== detail.sessionId
        )
      ],
      activeThreadId: detail.sessionId
    };
  }
  if (event.type === 'start') {
    const turn: AgentChatTurn = {
      id: event.id,
      prompt: event.prompt,
      answer: '',
      tools: [],
      error: null,
      done: false,
      requestCostUsd: null,
      sessionCostUsd: null
    };
    return updateActiveThread(state, thread => ({
      ...thread,
      title: thread.turns.length === 0 ? event.prompt.slice(0, 100) : thread.title,
      turns: [...thread.turns, turn],
      activeTurnId: event.id
    }));
  }
  if (event.type === 'stop') {
    return updateActiveThread(state, thread => ({...thread, activeTurnId: null}));
  }
  if (event.type === 'tool') {
    return updateActiveTurn(state, turn => ({...turn, tools: [...turn.tools, event]}));
  }
  if (event.type === 'delta') {
    return updateActiveTurn(state, turn => ({...turn, answer: turn.answer + event.text}));
  }
  if (event.type === 'usage') {
    return updateActiveTurn(state, turn => ({
      ...turn,
      requestCostUsd: event.estimatedCostUsd,
      sessionCostUsd: event.sessionCostUsd
    }));
  }
  if (event.type === 'error') {
    return updateActiveTurn(state, turn => ({...turn, error: event.message}));
  }
  if (event.type === 'done') {
    return updateActiveThread(
      updateActiveTurn(state, turn => ({...turn, done: true})),
      thread => ({...thread, activeTurnId: null})
    );
  }
  return state;
};

const clampThreadListWidth = (width: number, containerWidth?: number) => {
  const maxWidth = containerWidth ? Math.min(480, Math.max(260, containerWidth * 0.5)) : 480;
  return Math.min(Math.max(width, 180), maxWidth);
};

export const clampComposerHeight = (height: number) => Math.min(Math.max(height, 120), 360);

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const canCreateNewThread = (activeThread?: AgentChatThread) =>
  Boolean(activeThread && activeThread.turns.length > 0);

export const getThreadChannelName = (thread?: AgentChatThread) =>
  thread?.turns[0]?.prompt.slice(0, 100) || null;

const toScaledUsd = (value: string, scale: number) => {
  const [integerPart, decimalPart = ''] = value.split('.');
  const scaledDecimal = decimalPart.padEnd(scale, '0').slice(0, scale);
  return BigInt(`${integerPart}${scaledDecimal}`);
};

const fromScaledUsd = (value: bigint, scale: number) => {
  const sign = value < 0n ? '-' : '';
  const absolute = value < 0n ? -value : value;
  const digits = absolute.toString().padStart(scale + 1, '0');
  const integerPart = digits.slice(0, -scale);
  const decimalPart = digits.slice(-scale);
  return `${sign}${integerPart}.${decimalPart}`;
};

export const getThreadTotalCostUsd = (thread?: AgentChatThread) => {
  const costs = thread?.turns
    .map(turn => turn.requestCostUsd)
    .filter((cost): cost is string => Boolean(cost));
  if (!costs?.length) return null;

  const scale = Math.max(...costs.map(cost => cost.split('.')[1]?.length ?? 0), 6);
  const total = costs.reduce((sum, cost) => sum + toScaledUsd(cost, scale), 0n);
  return fromScaledUsd(total, scale);
};

const AgentChatPanel = () => {
  const {i18n, t} = useTranslation();
  const authUser = useAuthUser();
  const [prompt, setPrompt] = useState('');
  const [eventState, dispatchEvent] = useReducer(reduceAgentChatEvent, {
    threads: [],
    activeThreadId: null
  });
  const [loading, setLoading] = useState(false);
  const [threadHistory, setThreadHistory] = useState<PgHistoryAgentThreadSummary[]>([]);
  const [threadHistoryNextCursor, setThreadHistoryNextCursor] =
    useState<PgHistoryAgentPrivateThreadCursor | null>(null);
  const [threadHistoryLoading, setThreadHistoryLoading] = useState(false);
  const [threadHistoryLoadingMore, setThreadHistoryLoadingMore] = useState(false);
  const [threadHistoryError, setThreadHistoryError] = useState<string | null>(null);
  const [threadHistoryLoadMoreError, setThreadHistoryLoadMoreError] = useState<string | null>(null);
  const [sharedThreadHistory, setSharedThreadHistory] = useState<PgHistoryAgentThreadSummary[]>([]);
  const [sharedThreadHistoryNextCursor, setSharedThreadHistoryNextCursor] =
    useState<PgHistoryAgentSharedThreadCursor | null>(null);
  const [sharedThreadHistoryLoading, setSharedThreadHistoryLoading] = useState(false);
  const [sharedThreadHistoryLoadingMore, setSharedThreadHistoryLoadingMore] = useState(false);
  const [sharedThreadHistoryError, setSharedThreadHistoryError] = useState<string | null>(null);
  const [sharedThreadHistoryLoadMoreError, setSharedThreadHistoryLoadMoreError] = useState<
    string | null
  >(null);
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | null>(null);
  const [actionThread, setActionThread] = useState<PgHistoryAgentThreadSummary | null>(null);
  const [threadToDelete, setThreadToDelete] = useState<PgHistoryAgentThreadSummary | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [threadListWidth, setThreadListWidth] = useState(280);
  const [composerHeight, setComposerHeight] = useState(160);
  const [resizing, setResizing] = useState(false);
  const [composerResizing, setComposerResizing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const detailRequestRef = useRef<{
    sessionId: string;
    controller: AbortController;
  } | null>(null);
  const deletingSessionIdsRef = useRef(new Set<string>());
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const scrollEndRef = useRef<HTMLDivElement | null>(null);
  const threadHistoryRequestIdRef = useRef(0);
  const sharedThreadHistoryRequestIdRef = useRef(0);
  const activeThread = eventState.threads.find(thread => thread.id === eventState.activeThreadId);
  const threadTotalCostUsd = getThreadTotalCostUsd(activeThread);
  const activeThreadChannelName = getThreadChannelName(activeThread);

  const loadThreadHistory = useCallback(
    async (cursor?: PgHistoryAgentPrivateThreadCursor) => {
      const append = Boolean(cursor);
      const requestId = ++threadHistoryRequestIdRef.current;

      if (authUser?.companyId === undefined || !authUser?.userId) {
        setThreadHistory([]);
        setThreadHistoryNextCursor(null);
        setThreadHistoryLoading(false);
        setThreadHistoryLoadingMore(false);
        setThreadHistoryError(null);
        setThreadHistoryLoadMoreError(null);
        return;
      }

      if (append) {
        setThreadHistoryLoadingMore(true);
        setThreadHistoryLoadMoreError(null);
      } else {
        setThreadHistoryLoading(true);
        setThreadHistoryLoadingMore(false);
        setThreadHistoryError(null);
        setThreadHistoryLoadMoreError(null);
      }

      try {
        const response = await getPgHistoryAgentThreads(cursor);
        if (threadHistoryRequestIdRef.current !== requestId) return;

        if (append) {
          setThreadHistory(current => {
            const existingIds = new Set(current.map(thread => thread.sessionId));
            return [
              ...current,
              ...response.threads.filter(thread => !existingIds.has(thread.sessionId))
            ];
          });
        } else {
          setThreadHistory(response.threads);
        }
        setThreadHistoryNextCursor(response.nextCursor);
      } catch {
        if (threadHistoryRequestIdRef.current !== requestId) return;
        if (append) {
          setThreadHistoryLoadMoreError('調査履歴の読み込みに失敗しました');
        } else {
          setThreadHistoryError('調査履歴の読み込みに失敗しました');
        }
      } finally {
        if (threadHistoryRequestIdRef.current === requestId) {
          if (append) {
            setThreadHistoryLoadingMore(false);
          } else {
            setThreadHistoryLoading(false);
          }
        }
      }
    },
    [authUser?.companyId, authUser?.userId]
  );

  const loadSharedThreadHistory = useCallback(
    async (cursor?: PgHistoryAgentSharedThreadCursor) => {
      const append = Boolean(cursor);
      const requestId = ++sharedThreadHistoryRequestIdRef.current;

      if (authUser?.companyId === undefined || !authUser?.userId) {
        setSharedThreadHistory([]);
        setSharedThreadHistoryNextCursor(null);
        setSharedThreadHistoryLoading(false);
        setSharedThreadHistoryLoadingMore(false);
        setSharedThreadHistoryError(null);
        setSharedThreadHistoryLoadMoreError(null);
        return;
      }

      if (append) {
        setSharedThreadHistoryLoadingMore(true);
        setSharedThreadHistoryLoadMoreError(null);
      } else {
        setSharedThreadHistoryLoading(true);
        setSharedThreadHistoryLoadingMore(false);
        setSharedThreadHistoryError(null);
        setSharedThreadHistoryLoadMoreError(null);
      }

      try {
        const response = await getSharedPgHistoryAgentThreads(cursor);
        if (sharedThreadHistoryRequestIdRef.current !== requestId) return;
        if (append) {
          setSharedThreadHistory(current => {
            const existingIds = new Set(current.map(thread => thread.sessionId));
            return [
              ...current,
              ...response.threads.filter(thread => !existingIds.has(thread.sessionId))
            ];
          });
        } else {
          setSharedThreadHistory(response.threads);
        }
        setSharedThreadHistoryNextCursor(response.nextCursor);
      } catch {
        if (sharedThreadHistoryRequestIdRef.current !== requestId) return;
        if (append) {
          setSharedThreadHistoryLoadMoreError('調査履歴の読み込みに失敗しました');
        } else {
          setSharedThreadHistoryError('調査履歴の読み込みに失敗しました');
        }
      } finally {
        if (sharedThreadHistoryRequestIdRef.current === requestId) {
          if (append) {
            setSharedThreadHistoryLoadingMore(false);
          } else {
            setSharedThreadHistoryLoading(false);
          }
        }
      }
    },
    [authUser?.companyId, authUser?.userId]
  );

  const refreshHistories = useCallback(async () => {
    await Promise.all([loadThreadHistory(), loadSharedThreadHistory()]);
  }, [loadThreadHistory, loadSharedThreadHistory]);

  useLayoutEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    detailRequestRef.current = null;
    deletingSessionIdsRef.current.clear();
    dispatchEvent({type: 'reset'});
    setPrompt('');
    setLoading(false);
    setActionAnchor(null);
    setActionThread(null);
    setThreadToDelete(null);
    setActionError(null);
    threadHistoryRequestIdRef.current += 1;
    sharedThreadHistoryRequestIdRef.current += 1;
    setThreadHistory([]);
    setThreadHistoryNextCursor(null);
    setThreadHistoryLoading(false);
    setThreadHistoryLoadingMore(false);
    setThreadHistoryError(null);
    setThreadHistoryLoadMoreError(null);
    setSharedThreadHistory([]);
    setSharedThreadHistoryNextCursor(null);
    setSharedThreadHistoryLoading(false);
    setSharedThreadHistoryLoadingMore(false);
    setSharedThreadHistoryError(null);
    setSharedThreadHistoryLoadMoreError(null);
  }, [authUser?.companyId, authUser?.userId]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    void refreshHistories();

    return () => {
      threadHistoryRequestIdRef.current += 1;
      sharedThreadHistoryRequestIdRef.current += 1;
    };
  }, [refreshHistories]);

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({block: 'end'});
  }, [activeThread?.turns, loading]);

  useEffect(() => {
    if (authUser?.companyId === undefined || !authUser?.userId) return;
    try {
      localStorage.removeItem(`lfc-agent-chat-threads:${authUser.companyId}:${authUser.userId}`);
    } catch {
      // localStorage が無効な環境でも、サーバー履歴の描画を継続する。
    }
  }, [authUser?.companyId, authUser?.userId]);

  useEffect(() => {
    if (!resizing) return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const layout = layoutRef.current;
      if (!layout) return;
      const rect = layout.getBoundingClientRect();
      setThreadListWidth(clampThreadListWidth(event.clientX - rect.left, rect.width));
    };
    const handlePointerUp = () => {
      setResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizing]);

  useEffect(() => {
    if (!composerResizing) return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const thread = threadRef.current;
      if (!thread) return;
      const rect = thread.getBoundingClientRect();
      setComposerHeight(clampComposerHeight(rect.bottom - event.clientY));
    };
    const handlePointerUp = () => {
      setComposerResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [composerResizing]);

  const handleSubmit = async () => {
    const text = prompt.trim();
    if (!text || loading || activeThread?.readOnly) return;

    let thread = activeThread;
    if (!thread) {
      const sessionId = createSessionId();
      thread = {
        id: sessionId,
        sessionId,
        title: 'New Channel',
        createdAt: new Date().toLocaleString(),
        turns: [],
        activeTurnId: null
      };
      dispatchEvent({
        type: 'newThread',
        id: thread.id,
        sessionId: thread.sessionId,
        createdAt: thread.createdAt
      });
    }

    const turnId = createSessionId();
    setPrompt('');
    setLoading(true);
    dispatchEvent({type: 'start', id: turnId, prompt: text});
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamPgHistoryAgentChat(
        text,
        thread.sessionId,
        dispatchEvent,
        controller.signal,
        i18n.language?.startsWith('en') ? 'en' : 'ja'
      );
      await refreshHistories();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      dispatchEvent({type: 'error', message: t('AI調査の実行に失敗しました')});
      dispatchEvent({type: 'stop'});
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleNewThread = () => {
    if (!canCreateNewThread(activeThread)) return;

    abortRef.current?.abort();
    abortRef.current = null;
    setPrompt('');
    setLoading(false);
    const sessionId = createSessionId();
    dispatchEvent({
      type: 'newThread',
      id: sessionId,
      sessionId,
      createdAt: new Date().toLocaleString()
    });
  };

  const handleSelectThread = async (thread: PgHistoryAgentThreadSummary, readOnly: boolean) => {
    if (deletingSessionIdsRef.current.has(thread.sessionId)) return;
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    detailRequestRef.current = {sessionId: thread.sessionId, controller};
    setPrompt('');
    setLoading(true);

    try {
      const detail = await getPgHistoryAgentThread(thread.sessionId, controller.signal);
      if (
        controller.signal.aborted ||
        detailRequestRef.current?.controller !== controller ||
        deletingSessionIdsRef.current.has(thread.sessionId)
      ) {
        return;
      }
      dispatchEvent({type: 'restoreThread', detail, readOnly});
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setActionError('調査履歴の読み込みに失敗しました');
    } finally {
      if (detailRequestRef.current?.controller === controller) {
        detailRequestRef.current = null;
      }
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    detailRequestRef.current = null;
    dispatchEvent({type: 'stop'});
    setLoading(false);
  };

  const abortPendingThreadDetail = (sessionId: string) => {
    const pending = detailRequestRef.current;
    if (!pending || pending.sessionId !== sessionId) return;
    pending.controller.abort();
    detailRequestRef.current = null;
    if (abortRef.current === pending.controller) {
      abortRef.current = null;
      setLoading(false);
    }
  };

  const handleOpenActions = (
    event: ReactMouseEvent<HTMLElement>,
    thread: PgHistoryAgentThreadSummary
  ) => {
    event.stopPropagation();
    setActionError(null);
    setActionAnchor(event.currentTarget);
    setActionThread(thread);
  };

  const handleCloseActions = () => {
    setActionAnchor(null);
    setActionThread(null);
  };

  const handleChangeVisibility = async (visibility: 'private' | 'company') => {
    if (!actionThread) return;
    const {sessionId} = actionThread;
    handleCloseActions();
    try {
      await setPgHistoryAgentThreadVisibility(sessionId, visibility);
      await refreshHistories();
    } catch {
      setActionError('履歴の操作に失敗しました');
    }
  };

  const handleConfirmDelete = async () => {
    if (!threadToDelete) return;
    const {sessionId} = threadToDelete;
    setThreadToDelete(null);
    deletingSessionIdsRef.current.add(sessionId);
    abortPendingThreadDetail(sessionId);
    try {
      await deletePgHistoryAgentThread(sessionId);
      abortPendingThreadDetail(sessionId);
      dispatchEvent({type: 'removeThread', id: sessionId});
      setThreadHistory(current => current.filter(thread => thread.sessionId !== sessionId));
      setSharedThreadHistory(current => current.filter(thread => thread.sessionId !== sessionId));
      await refreshHistories();
    } catch {
      deletingSessionIdsRef.current.delete(sessionId);
      setActionError('履歴の操作に失敗しました');
    }
  };

  const isDeleteDisabled = (thread: PgHistoryAgentThreadSummary) =>
    thread.status !== 'completed' && thread.status !== 'failed';

  const showWelcome = !activeThread || activeThread.turns.length === 0;
  const renderPromptInput = (centered: boolean) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: centered ? 2 : 1,
        flexShrink: 0,
        height: centered ? 'auto' : composerHeight,
        width: centered ? 'min(720px, 100%)' : '100%'
      }}
    >
      <TextField
        name="comment"
        label="Message"
        fullWidth
        multiline
        rows={centered ? 3 : undefined}
        minRows={centered ? undefined : 2}
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        placeholder={t('ここ1週間で変更されたファイルを探して変更内容を説明して')}
        onKeyDown={e => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            handleSubmit();
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: centered ? 'action.hover' : 'background.paper',
            height: centered ? 'auto' : '100%',
            alignItems: centered ? 'center' : 'stretch'
          },
          '& textarea': {
            overflow: 'auto'
          }
        }}
      />
      <Box sx={{display: 'flex', gap: 1, justifyContent: centered ? 'flex-end' : 'flex-start'}}>
        <Button
          color="primary"
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={loading || prompt.trim().length === 0}
        >
          {t('送信')}
        </Button>
        {loading && (
          <Tooltip title={t('停止')}>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<StopIcon />}
              onClick={handleStop}
            >
              {t('停止')}
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      ref={layoutRef}
      sx={{
        height: '100%',
        display: 'flex',
        overflow: 'hidden',
        userSelect: resizing || composerResizing ? 'none' : 'auto',
        cursor: resizing ? 'col-resize' : composerResizing ? 'row-resize' : 'auto'
      }}
    >
      <Box
        sx={{
          width: threadListWidth,
          minWidth: 180,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          p: 1
        }}
      >
        <List dense sx={{flexShrink: 0}}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleNewThread} sx={{borderRadius: 1, px: 1, py: 0.5}}>
              <AddCircleOutlineIcon fontSize="small" sx={{mr: 1, color: 'text.secondary'}} />
              <ListItemText
                primary={t('新規チャット')}
                primaryTypographyProps={{variant: 'body2', noWrap: true}}
              />
            </ListItemButton>
          </ListItem>
        </List>
        <Accordion disableGutters elevation={0} sx={{flexShrink: 0, bgcolor: 'transparent'}}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{minHeight: 36, px: 1}}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{fontWeight: 700}}>
                {t('共有スレッド')}
              </Typography>
              <Chip size="small" label={sharedThreadHistory.length} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{p: 0}}>
            <List dense sx={{maxHeight: '30%', overflow: 'auto'}}>
              {sharedThreadHistoryLoading && (
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', px: 1}}>
                  {t('調査履歴を読み込んでいます')}
                </Typography>
              )}
              {!sharedThreadHistoryLoading && sharedThreadHistoryError && (
                <Typography variant="caption" color="error" sx={{display: 'block', px: 1}}>
                  {t(sharedThreadHistoryError)}
                </Typography>
              )}
              {!sharedThreadHistoryLoading &&
                !sharedThreadHistoryError &&
                sharedThreadHistory.length === 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{display: 'block', px: 1}}
                  >
                    {t('共有スレッドはありません')}
                  </Typography>
                )}
              {!sharedThreadHistoryLoading &&
                !sharedThreadHistoryError &&
                sharedThreadHistory.map(thread => (
                  <ListItem key={thread.sessionId} disablePadding>
                    <ListItemButton
                      aria-label={thread.title}
                      selected={thread.sessionId === eventState.activeThreadId}
                      onClick={() => void handleSelectThread(thread, true)}
                      sx={{borderRadius: 1, px: 1, py: 0.5}}
                    >
                      <ListItemText
                        primary={thread.title}
                        secondary={`${t('共有者')} ${
                          thread.sharedByUserId || thread.ownerUserId || '-'
                        } · ${t('共有日時')} ${
                          thread.sharedAt ? new Date(thread.sharedAt * 1000).toLocaleString() : '-'
                        } · ${t(jobStatusTranslationKey[thread.status])}`}
                        primaryTypographyProps={{variant: 'body2', noWrap: true}}
                        secondaryTypographyProps={{variant: 'caption', noWrap: true}}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              {!sharedThreadHistoryLoading &&
                !sharedThreadHistoryError &&
                sharedThreadHistoryNextCursor && (
                  <ListItem disablePadding>
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => void loadSharedThreadHistory(sharedThreadHistoryNextCursor)}
                      disabled={sharedThreadHistoryLoadingMore}
                    >
                      {t(
                        sharedThreadHistoryLoadingMore
                          ? '読み込み中'
                          : '共有スレッドをさらに読み込む'
                      )}
                    </Button>
                  </ListItem>
                )}
              {!sharedThreadHistoryLoading && sharedThreadHistoryLoadMoreError && (
                <Typography variant="caption" color="error" sx={{display: 'block', px: 1}}>
                  {t(sharedThreadHistoryLoadMoreError)}
                </Typography>
              )}
            </List>
          </AccordionDetails>
        </Accordion>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{display: 'block', px: 1, pt: 2, pb: 0.75, fontWeight: 700}}
        >
          {t('履歴')}
        </Typography>
        <List dense sx={{maxHeight: '40%', flexShrink: 0, overflow: 'auto'}}>
          {threadHistoryLoading && (
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', px: 1}}>
              {t('調査履歴を読み込んでいます')}
            </Typography>
          )}
          {!threadHistoryLoading && threadHistoryError && (
            <Typography variant="caption" color="error" sx={{display: 'block', px: 1}}>
              {t(threadHistoryError)}
            </Typography>
          )}
          {!threadHistoryLoading && !threadHistoryError && threadHistory.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', px: 1}}>
              {t('調査履歴はありません')}
            </Typography>
          )}
          {!threadHistoryLoading &&
            !threadHistoryError &&
            threadHistory.map(thread => (
              <ListItem
                key={thread.sessionId}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={`${thread.title} ${t('操作')}`}
                    onClick={event => handleOpenActions(event, thread)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  aria-label={thread.title}
                  selected={thread.sessionId === eventState.activeThreadId}
                  onClick={() => void handleSelectThread(thread, false)}
                  sx={{borderRadius: 1, px: 1, py: 0.5}}
                >
                  <ListItemText
                    primary={thread.title}
                    secondary={`${t(jobStatusTranslationKey[thread.status])} · ${new Date(
                      thread.createdAt * 1000
                    ).toLocaleString()}`}
                    primaryTypographyProps={{variant: 'body2', noWrap: true}}
                    secondaryTypographyProps={{variant: 'caption', noWrap: true}}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          {!threadHistoryLoading && !threadHistoryError && threadHistoryNextCursor && (
            <ListItem disablePadding>
              <Button
                fullWidth
                size="small"
                onClick={() => void loadThreadHistory(threadHistoryNextCursor)}
                disabled={threadHistoryLoadingMore}
              >
                {t(threadHistoryLoadingMore ? '読み込み中' : '履歴をさらに読み込む')}
              </Button>
            </ListItem>
          )}
          {!threadHistoryLoading && threadHistoryLoadMoreError && (
            <Typography variant="caption" color="error" sx={{display: 'block', px: 1}}>
              {t(threadHistoryLoadMoreError)}
            </Typography>
          )}
        </List>
      </Box>

      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label={t('Thread Listの幅を変更')}
        onPointerDown={event => {
          event.preventDefault();
          setResizing(true);
        }}
        sx={{
          width: 7,
          flexShrink: 0,
          cursor: 'col-resize',
          touchAction: 'none',
          borderLeft: 1,
          borderRight: 1,
          borderColor: resizing ? 'primary.main' : 'divider',
          bgcolor: resizing ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main'
          }
        }}
      />

      <Box ref={threadRef} sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column'}}>
        <Box sx={{px: 2, py: 1, flexShrink: 0}}>
          <Stack direction="row" spacing={1} alignItems="center">
            {activeThreadChannelName && (
              <Typography variant="subtitle1" noWrap sx={{minWidth: 0, fontWeight: 600}}>
                {activeThreadChannelName}
              </Typography>
            )}
            {threadTotalCostUsd && (
              <Chip
                size="small"
                variant="outlined"
                label={`${t('セッション累計')} $${threadTotalCostUsd}`}
              />
            )}
          </Stack>
        </Box>

        <Box
          sx={{flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column'}}
        >
          {showWelcome ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                py: 4
              }}
            >
              <Box sx={{width: 'min(760px, 100%)'}}>
                <Typography
                  variant="h4"
                  color="text.primary"
                  sx={{mb: 3, textAlign: 'center', fontWeight: 400}}
                >
                  {t('AI調査で何を調べますか？')}
                </Typography>
                {renderPromptInput(true)}
              </Box>
            </Box>
          ) : (
            <Stack spacing={2} sx={{px: 2, py: 2}}>
              {(activeThread?.turns ?? []).map(turn => {
                const isActive = turn.id === activeThread?.activeTurnId;
                const isGenerating = isActive && loading;

                return (
                  <Stack key={turn.id} spacing={1.5}>
                    <Box
                      role="article"
                      aria-label={t('ユーザーのメッセージ')}
                      sx={{
                        alignSelf: 'flex-end',
                        maxWidth: 'min(80%, 760px)',
                        px: 2,
                        py: 1.25,
                        borderRadius: 2,
                        bgcolor: 'success.main',
                        color: 'success.contrastText'
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        component="div"
                        sx={{mb: 0.5, fontWeight: 600}}
                      >
                        {t('ユーザー')}
                      </Typography>
                      <Typography sx={{whiteSpace: 'pre-line', wordBreak: 'break-word'}}>
                        {turn.prompt}
                      </Typography>
                    </Box>

                    <Box
                      role="article"
                      aria-label={t('アシスタントの結果')}
                      sx={{
                        alignSelf: 'flex-start',
                        width: 'min(100%, 920px)',
                        minWidth: 0,
                        px: 2,
                        py: 1.5,
                        border: 1,
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                        <Typography
                          variant="subtitle2"
                          component="div"
                          color="primary"
                          sx={{fontWeight: 600}}
                        >
                          {t('アシスタント')}
                        </Typography>
                        {isGenerating && (
                          <Box
                            role="status"
                            aria-label={t('アシスタントが応答を生成中')}
                            sx={{display: 'inline-flex'}}
                          >
                            <CircularProgress size={14} />
                          </Box>
                        )}
                      </Box>
                      {turn.tools.length > 0 && (
                        <AgentToolLog events={turn.tools} isActive={isActive} />
                      )}
                      {turn.error ? (
                        <Alert severity="error">{turn.error}</Alert>
                      ) : turn.answer ? (
                        <AiMarkdownViewer
                          content={turn.answer}
                          loading={false}
                          fill={false}
                          variant={turn.done ? 'result' : 'chat'}
                        />
                      ) : isGenerating ? (
                        <Skeleton aria-hidden="true" variant="rounded" height={60} />
                      ) : null}
                      {turn.requestCostUsd && (
                        <AgentCostBadge
                          requestCostUsd={turn.requestCostUsd}
                          sessionCostUsd={turn.sessionCostUsd}
                          requestLabel={t('今回')}
                          showSessionCost={false}
                        />
                      )}
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          )}
          <Box ref={scrollEndRef} />
        </Box>

        {!showWelcome && !activeThread?.readOnly && (
          <>
            <Box
              role="separator"
              aria-orientation="horizontal"
              aria-label={t('Messageの高さを変更')}
              onPointerDown={event => {
                event.preventDefault();
                setComposerResizing(true);
              }}
              sx={{
                height: 7,
                flexShrink: 0,
                cursor: 'row-resize',
                touchAction: 'none',
                borderTop: 1,
                borderBottom: 1,
                borderColor: composerResizing ? 'primary.main' : 'divider',
                bgcolor: composerResizing ? 'action.selected' : 'transparent',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
            />
            {renderPromptInput(false)}
          </>
        )}
      </Box>

      <Menu anchorEl={actionAnchor} open={Boolean(actionAnchor)} onClose={handleCloseActions}>
        {actionThread && (
          <>
            <MenuItem
              onClick={() =>
                void handleChangeVisibility(
                  actionThread.visibility === 'company' ? 'private' : 'company'
                )
              }
            >
              {t(actionThread.visibility === 'company' ? '共有解除する' : '共有する')}
            </MenuItem>
            <MenuItem
              disabled={isDeleteDisabled(actionThread)}
              onClick={() => {
                if (isDeleteDisabled(actionThread)) return;
                setThreadToDelete(actionThread);
                handleCloseActions();
              }}
            >
              {t('削除')}
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog
        open={Boolean(threadToDelete)}
        onClose={() => setThreadToDelete(null)}
        aria-labelledby="delete-job-dialog-title"
      >
        <DialogTitle id="delete-job-dialog-title">{t('調査履歴を削除しますか？')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('この操作は取り消せません')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThreadToDelete(null)}>{t('キャンセル')}</Button>
          <Button color="error" onClick={() => void handleConfirmDelete()}>
            {t('削除する')}
          </Button>
        </DialogActions>
      </Dialog>
      {actionError && (
        <Alert
          severity="error"
          onClose={() => setActionError(null)}
          sx={{position: 'absolute', right: 16, bottom: 16}}
        >
          {t(actionError)}
        </Alert>
      )}
    </Box>
  );
};

export default AgentChatPanel;
